/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `ChatInputBar` for the Caintra Connect frontend.
 * @module components/Chat/ChatInputBar
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { ReactComponent as SendIcon } from "../../assets/icons/Chat/SendIcon.svg";
import { ReactComponent as AttachFilesIcon } from "../../assets/icons/Chat/AttachFilesIcon.svg";

const ChatInputBar = ({
  t,
  styles,
  readOnly,
  isFileUploading,
  messageInput,
  onAttachFilesClick,
  onMessageInputChange,
  onKeyPress,
  onSendMessage,
}) => (
  <div className={styles.chatInputContainer}>
    <button
      className={`${styles.attachFilesButton} ${
        isFileUploading ? styles.attachFilesButtonDisabled : ""
      }`}
      onClick={onAttachFilesClick}
      title={t("pages.chat.attachFiles")}
      disabled={isFileUploading}
    >
      <AttachFilesIcon className={styles.attachFilesIcon} />
    </button>
    <input
      type="text"
      placeholder={
        isFileUploading
          ? t("pages.chat.uploadingFile")
          : t("pages.chat.inputPlaceholder")
      }
      className={`${styles.chatInput} ${
        isFileUploading ? styles.chatInputDisabled : ""
      }`}
      value={messageInput}
      onChange={onMessageInputChange}
      onKeyPress={onKeyPress}
      disabled={isFileUploading}
    />
    <button
      className={`${styles.sendButton} ${
        isFileUploading ? styles.sendButtonLoading : ""
      }`}
      onClick={onSendMessage}
      disabled={isFileUploading || readOnly}
    >
      {isFileUploading ? (
        <div className={styles.loadingSpinner}></div>
      ) : (
        <SendIcon className={styles.sendIcon} />
      )}
    </button>
  </div>
);

export default ChatInputBar;
